/**
 * Position and size of a floating window inside the viewport. Pure, so `node --test` covers it.
 * A rect is `{ left, top, width, height }` in CSS pixels; bounds are `{ width, height }`.
 */

export const WINDOW_EDGE_GAP = 8;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

/** Keep the whole window on screen, shrinking it first when the viewport is smaller. */
export function clampRect(rect, bounds, min = { width: 480, height: 360 }) {
  const gap = WINDOW_EDGE_GAP;
  const maxWidth = Math.max(1, bounds.width - gap * 2);
  const maxHeight = Math.max(1, bounds.height - gap * 2);
  const width = clamp(rect.width, Math.min(min.width, maxWidth), maxWidth);
  const height = clamp(rect.height, Math.min(min.height, maxHeight), maxHeight);
  return {
    width,
    height,
    left: clamp(rect.left, gap, bounds.width - width - gap),
    top: clamp(rect.top, gap, bounds.height - height - gap),
  };
}

/** A window of the preferred size, centred. */
export function centeredRect(preferred, bounds, min) {
  const width = Math.min(preferred.width, bounds.width - WINDOW_EDGE_GAP * 2);
  const height = Math.min(preferred.height, bounds.height - WINDOW_EDGE_GAP * 2);
  return clampRect(
    { width, height, left: (bounds.width - width) / 2, top: (bounds.height - height) / 2 },
    bounds,
    min,
  );
}

/** The window moved by (dx, dy) from where the drag started. */
export function moveRect(start, dx, dy, bounds, min) {
  return clampRect({ ...start, left: start.left + dx, top: start.top + dy }, bounds, min);
}

/**
 * The window resized from the edge or corner `direction` ('n', 's', 'e', 'w', 'ne', 'nw', 'se',
 * 'sw') by (dx, dy). The opposite edge stays where it was, also when the minimum size is reached.
 */
export function resizeRect(start, direction, dx, dy, bounds, min = { width: 480, height: 360 }) {
  const gap = WINDOW_EDGE_GAP;
  const right = start.left + start.width;
  const bottom = start.top + start.height;
  let { left, top, width, height } = start;

  if (direction.includes('e')) {
    width = clamp(start.width + dx, min.width, bounds.width - gap - start.left);
  }
  if (direction.includes('w')) {
    left = clamp(start.left + dx, gap, right - min.width);
    width = right - left;
  }
  if (direction.includes('s')) {
    height = clamp(start.height + dy, min.height, bounds.height - gap - start.top);
  }
  if (direction.includes('n')) {
    top = clamp(start.top + dy, gap, bottom - min.height);
    height = bottom - top;
  }
  return { left, top, width, height };
}

/** Width of a side panel dragged by its left edge, leaving `reserve` px for the content beside it. */
export function resizePanelWidth(startWidth, dx, containerWidth, { min = 280, reserve = 320 } = {}) {
  return clamp(startWidth - dx, min, containerWidth - reserve);
}
